/**
 * Startup Dashboard Stats Hook
 *
 * Fetches aggregated stats (profile views, interested investors,
 * data room views, profile completeness) from the startup-dashboard-stats
 * edge function.
 *
 * In mock/demo mode (showSampleData), returns hardcoded sample values.
 * In production, calls the real edge function.
 */

import { useQuery } from '@tanstack/react-query';
import { callEdgeFunction } from '@/lib/supabase/edge-functions';
import { mode } from '@/lib/config/app-mode';

export interface StartupDashboardStats {
  profileViews: number;
  profileViewsTrend: number;
  interestedInvestors: number;
  interestedInvestorsTrend: number;
  dataRoomViews: number;
  dataRoomViewsTrend: number;
  profileCompleteness: number;
}

/** Sample stats shown in mock/demo mode (matches original mockDashboardStats values) */
const SAMPLE_STATS: StartupDashboardStats = {
  profileViews: 47,
  profileViewsTrend: 12,
  interestedInvestors: 5,
  interestedInvestorsTrend: 2,
  dataRoomViews: 23,
  dataRoomViewsTrend: 8,
  profileCompleteness: 78,
};

export function useStartupDashboardStats(userId: string | undefined) {
  return useQuery({
    queryKey: ['startup-dashboard-stats', userId],
    queryFn: async (): Promise<StartupDashboardStats> => {
      // In mock/demo mode, return sample data without hitting the edge function
      if (mode.showSampleData) {
        return SAMPLE_STATS;
      }

      const { data, error } = await callEdgeFunction<StartupDashboardStats>(
        'startup-dashboard-stats',
        {},
        { dedupe: true },
      );

      if (error || !data) {
        throw new Error(error || 'Failed to fetch dashboard stats');
      }

      return data;
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
